import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle, XCircle, ChevronRight, Award, RotateCcw, BookOpen } from 'lucide-react';
import { quizQuestions, curriculum } from '../data/mock';
import { submitQuiz } from '../services/api';
import { toast } from 'sonner';

const Quiz = ({ moduleId = 1, onModuleChange, isEnrolled = false }) => {
  const [currentModule, setCurrentModule] = useState(moduleId);
  const questions = quizQuestions[`module${currentModule}`] || quizQuestions.module1;
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [quizComplete, setQuizComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset quiz when module changes
  useEffect(() => {
    setCurrentModule(moduleId);
    handleRestart();
  }, [moduleId]);

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + (showResult ? 1 : 0)) / questions.length) * 100;
  const passingScore = Math.ceil(questions.length * 0.8);

  const handleModuleChange = (value) => {
    const newModule = parseInt(value);
    setCurrentModule(newModule);
    onModuleChange && onModuleChange(newModule);
    handleRestart();
  };

  const handleSubmit = () => {
    if (selectedAnswer === null) return;
    
    const isCorrect = selectedAnswer === question.correctAnswer;
    setAnswers(prev => [...prev, { questionId: question.id, isCorrect }]);
    
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
    setShowResult(true);
  };

  const handleNext = async () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      // Quiz complete - submit to backend
      const finalScore = score + (selectedAnswer === question.correctAnswer ? 1 : 0);
      const passed = finalScore >= passingScore;
      
      // Try to submit to backend if user is enrolled
      const leadId = localStorage.getItem('oshaLeadId');
      if (leadId && isEnrolled) {
        setIsSubmitting(true);
        try {
          await submitQuiz({
            lead_id: leadId,
            module_id: currentModule,
            score: finalScore,
            total_questions: questions.length,
            passed: passed
          });
          toast.success(passed ? 'Quiz passed! Progress saved.' : 'Progress saved. Keep learning!');
        } catch (error) {
          console.error('Error submitting quiz:', error);
        } finally {
          setIsSubmitting(false);
        }
      }
      
      setQuizComplete(true);
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setAnswers([]);
    setQuizComplete(false);
  };

  const finalScore = quizComplete ? score : score;
  const passed = finalScore >= passingScore;

  if (quizComplete) {
    return (
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <Card className="max-w-2xl mx-auto p-8 text-center">
            <div className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${
              passed ? 'bg-emerald-100' : 'bg-amber-100'
            }`}>
              {passed ? (
                <Award className="w-10 h-10 text-emerald-600" />
              ) : (
                <BookOpen className="w-10 h-10 text-amber-600" />
              )}
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {passed ? 'Congratulations!' : 'Keep Learning!'}
            </h2>
            
            <p className="text-slate-600 mb-2">
              Module {currentModule}: {curriculum[currentModule - 1]?.title}
            </p>
            
            <p className="text-slate-600 mb-6">
              {passed 
                ? `You passed the quiz with ${score}/${questions.length} correct answers!`
                : `You scored ${score}/${questions.length}. You need ${passingScore} correct answers to pass.`
              }
            </p>

            <div className="flex justify-center gap-4 flex-wrap">
              <Button
                variant="outline"
                onClick={handleRestart}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              {passed && currentModule < 8 && (
                <Button 
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  onClick={() => handleModuleChange(String(currentModule + 1))}
                >
                  Next Module Quiz
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-slate-50">
      <div className="container mx-auto px-6">
        {/* Section header with module selector */}
        <div className="text-center mb-8">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Test Your Knowledge</span>
          <h2 className="text-3xl font-bold text-slate-900 mt-2 mb-4">
            Module Quiz
          </h2>
          
          {/* Module Selector */}
          <div className="flex justify-center mb-4">
            <Select value={String(currentModule)} onValueChange={handleModuleChange}>
              <SelectTrigger className="w-72">
                <SelectValue placeholder="Select a module" />
              </SelectTrigger>
              <SelectContent>
                {curriculum.map((module, index) => (
                  <SelectItem key={module.id} value={String(module.id)}>
                    Module {module.id}: {module.title.length > 30 ? module.title.substring(0, 30) + '...' : module.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <p className="text-slate-600 text-sm">
            {curriculum[currentModule - 1]?.title}
          </p>
        </div>

        {/* Progress */}
        <div className="max-w-2xl mx-auto mb-6">
          <div className="flex justify-between text-sm text-slate-600 mb-2">
            <span>Question {currentQuestion + 1} of {questions.length}</span>
            <span>Score: {score}/{currentQuestion + (showResult ? 1 : 0)}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question card */}
        <Card className="max-w-2xl mx-auto p-8">
          <h3 className="text-xl font-semibold text-slate-900 mb-6">
            {question.question}
          </h3>

          <RadioGroup 
            value={selectedAnswer?.toString()} 
            onValueChange={(value) => !showResult && setSelectedAnswer(parseInt(value))}
            className="space-y-3"
          >
            {question.options.map((option, index) => {
              const isSelected = selectedAnswer === index;
              const isCorrect = index === question.correctAnswer;
              
              let optionClass = 'border-slate-200 hover:border-emerald-300';
              if (showResult) {
                if (isCorrect) {
                  optionClass = 'border-emerald-500 bg-emerald-50';
                } else if (isSelected && !isCorrect) {
                  optionClass = 'border-red-500 bg-red-50';
                } else {
                  optionClass = 'border-slate-200 opacity-50';
                }
              }

              return (
                <div
                  key={index}
                  className={`flex items-center space-x-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${optionClass}`}
                  onClick={() => !showResult && setSelectedAnswer(index)}
                >
                  <RadioGroupItem 
                    value={index.toString()} 
                    id={`option-${index}`}
                    disabled={showResult}
                  />
                  <Label 
                    htmlFor={`option-${index}`} 
                    className="flex-1 cursor-pointer text-slate-700"
                  >
                    {option}
                  </Label>
                  {showResult && isCorrect && (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  )}
                  {showResult && isSelected && !isCorrect && (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                </div>
              );
            })}
          </RadioGroup>

          {/* Explanation */}
          {showResult && (
            <div className={`mt-6 p-4 rounded-xl ${
              selectedAnswer === question.correctAnswer 
                ? 'bg-emerald-50 border border-emerald-200' 
                : 'bg-amber-50 border border-amber-200'
            }`}>
              <p className={`text-sm ${
                selectedAnswer === question.correctAnswer 
                  ? 'text-emerald-700' 
                  : 'text-amber-700'
              }`}>
                {question.explanation}
              </p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
            <Button
              variant="outline"
              onClick={handleRestart}
              className="text-slate-600"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Restart
            </Button>
            
            {!showResult ? (
              <Button
                onClick={handleSubmit}
                disabled={selectedAnswer === null}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                Submit Answer
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={isSubmitting}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                {isSubmitting ? 'Saving...' : currentQuestion < questions.length - 1 ? 'Next Question' : 'See Results'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </Card>
      </div>
    </section>
  );
};

export default Quiz;
